import { useState } from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { useLang, useContent, loc, UI, INTERVIEW_FEATURES, INTERVIEW_CARDS } from "@/lib/content";
import { resolveImg } from "@/lib/api";

function FlipCard({ card, i }) {
  const [flipped, setFlipped] = useState(false);
  const { lang } = useLang();
  return (
    <motion.button
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
      data-testid={`interview-card-${i}`}
      onClick={() => setFlipped((v) => !v)}
      className={`flip-card h-60 w-full text-left ${flipped ? "flipped" : ""}`}
    >
      <div className="flip-inner relative h-full w-full">
        <div className="flip-face absolute inset-0 flex flex-col justify-between rounded-2xl border border-linelight bg-white/5 p-6">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-brass">
            {lang === "es" ? `Pregunta ${String(i + 1).padStart(2, "0")}` : `Question ${String(i + 1).padStart(2, "0")}`}
          </span>
          <p className="font-display text-xl font-medium leading-snug text-paper">“{loc(card.q, lang)}”</p>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50">
            {lang === "es" ? "Toca para ver el consejo →" : "Tap to see the tip →"}
          </span>
        </div>
        <div className="flip-face flip-back absolute inset-0 flex flex-col justify-between rounded-2xl border border-brass/50 bg-brass p-6">
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-navy/70">
            {lang === "es" ? "Consejo Cívica" : "Cívica tip"}
          </span>
          <p className="text-sm font-semibold leading-relaxed text-navy">{loc(card.tip, lang)}</p>
        </div>
      </div>
    </motion.button>
  );
}

export default function Interview() {
  const { slot } = useContent();
  const { lang } = useLang();
  const img = slot("entrevista");

  return (
    <section id="entrevista" className="relative overflow-hidden bg-navy py-28 text-paper">
      <div className="pointer-events-none absolute -right-24 top-16 opacity-[0.06]">
        <svg width="420" height="420" viewBox="0 0 100 100" fill="none">
          <path d="M50 5l11.2 22.7 25.1 3.6-18.2 17.7 4.3 25L50 62.2l-22.4 11.8 4.3-25L13.7 31.3l25.1-3.6L50 5z" stroke="#FBF9F4" strokeWidth="1.2" fill="none" />
        </svg>
      </div>
      <span className="absolute left-0 top-0 h-full w-1.5 bg-heritage" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-brass">
            <span className="h-px w-8 bg-brass" /> {loc(UI.interviewHeader.overline, lang)}
          </p>
          <h2 className="mt-4 font-display text-5xl font-semibold tracking-tight sm:text-6xl">
            {loc(UI.interviewHeader.title, lang)}
            <span className="italic text-brass">{loc(UI.interviewHeader.titleAccent, lang)}</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-paper/75 sm:text-lg">{loc(UI.interviewHeader.intro, lang)}</p>
        </motion.div>

        <div className="mt-16 grid items-start gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="frame-arch absolute -inset-3 -translate-x-3 translate-y-3 border-2 border-brass/40" />
            <div className="frame-arch relative aspect-[4/5] overflow-hidden border border-linelight bg-navydeep">
              {img.visible && img.url ? (
                <img src={resolveImg(img.url)} alt={lang === "es" ? "Entrevista de naturalización" : "Naturalization interview"} data-testid="interview-image" className="h-full w-full object-cover opacity-90" />
              ) : (
                <div className="h-full w-full bg-navydeep" data-testid="interview-image-placeholder" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent" />
            </div>
            <div className="absolute bottom-8 -right-3 max-w-[16rem] rounded-2xl border border-linelight bg-navydeep/95 p-5 shadow-2xl backdrop-blur sm:right-6">
              <Quote size={16} className="text-brass" />
              <p className="mt-2 text-[13px] font-medium leading-relaxed text-paper/85">{loc(UI.interviewHeader.quote, lang)}</p>
            </div>
          </motion.div>

          <div>
            {INTERVIEW_FEATURES.map((f, i) => (
              <motion.div
                key={f.num}
                initial={{ opacity: 0, x: 28 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                data-testid={`interview-feature-${i + 1}`}
                className="group flex gap-5 border-b border-linelight py-5 transition-all duration-300 first:pt-0 hover:pl-2"
              >
                <span className="font-mono text-sm font-semibold text-brass">{f.num}</span>
                <div>
                  <h3 className="text-lg font-bold text-paper">{loc(f.title, lang)}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-paper/65">{loc(f.desc, lang)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-20">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-paper/60"
          >
            {loc(UI.interviewHeader.cardsLabel, lang)}
          </motion.p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {INTERVIEW_CARDS.map((card, i) => (
              <FlipCard key={i} card={card} i={i} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
